import { useEffect, useRef, useState } from "react";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, ExternalLink } from "lucide-react";

// Placeholder URL — replace with your real Calendly link.
export const DEFAULT_CALENDLY_URL = "https://calendly.com/vikas-savant/new-meeting";

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";

function loadCalendlyScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Calendly) return Promise.resolve();
  if (document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`)) {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (window.Calendly) { clearInterval(check); resolve(); }
      }, 80);
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = CALENDLY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Calendly widget"));
    document.head.appendChild(s);
  });
}

export default function CalendlyEmbed({ url = DEFAULT_CALENDLY_URL }) {
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setErrored(false);

    loadCalendlyScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.Calendly) return;
        containerRef.current.innerHTML = "";
        const primary = theme === "dark" ? "3366FF" : "0047FF";
        const bg = theme === "dark" ? "09090b" : "ffffff";
        const text = theme === "dark" ? "fafafa" : "0a0a0a";
        window.Calendly.initInlineWidget({
          url: `${url}?hide_gdpr_banner=1&primary_color=${primary}&background_color=${bg}&text_color=${text}`,
          parentElement: containerRef.current,
        });
        setReady(true);
      })
      .catch(() => setErrored(true));

    return () => { cancelled = true; };
  }, [url, theme]);

  if (errored) {
    return (
      <div data-testid="calendly-fallback" className="border border-border rounded-md p-8 bg-card text-center">
        <Calendar className="h-8 w-8 text-saavi-blue mx-auto" />
        <h3 className="mt-4 text-xl">Couldn't load the booking widget</h3>
        <p className="mt-2 text-sm text-muted-foreground">Open Calendly directly to book your call.</p>
        <a href={url} target="_blank" rel="noreferrer">
          <Button className="mt-5 h-11 gap-2" data-testid="calendly-fallback-link">
            Book on Calendly <ExternalLink className="h-4 w-4" />
          </Button>
        </a>
      </div>
    );
  }

  return (
    <div className="relative border border-border rounded-md overflow-hidden bg-card" data-testid="calendly-embed-wrapper">
      {!ready && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10 bg-card">
          <Loader2 className="h-6 w-6 animate-spin text-saavi-blue" />
          <span className="font-mono-overline text-muted-foreground">Loading Calendar…</span>
        </div>
      )}
      <div
        ref={containerRef}
        data-testid="calendly-inline-widget"
        style={{ minWidth: 320, height: 720 }}
      />
    </div>
  );
}
